import S, { DataSignal } from 's-js';
import SArray, { lift } from 's-array';
import * as BBPromise from 'bluebird';
import { IAjax } from 'lib/Ajax';
import { DraftObject } from 'lib/draftSignals';
import { AutoSaver } from 'lib/autoSave';
import uniqueIndex, { UniqueIndex } from 'lib/uniqueIndex';
import nonuniqueIndex, { NonuniqueIndex } from 'lib/nonuniqueIndex';

import { PCCache } from 'data/PCCache';

import { DataCacheUpdateEntities } from 'data/models/DataCacheUpdate';
import { Pilot } from 'data/models/Pilot';
import { PilotAssignment, PilotAssignmentOptions } from 'data/models/PilotAssignment';
import { PilotJobOrderDetails as ServerPilotJobOrderDetails } from 'data/models/PilotJobOrderDetails';
import { PilotSection } from 'data/models/PilotSection';

export interface PilotAssignmentsParams {
    model : ServerPilotJobOrderDetails;
}

export function PilotAssignments(
    ajax : IAjax,
    data : PCCache,
    { model } : PilotAssignmentsParams
) {
    const
        pilotAgency = () => model.PilotJobOrder() && model.PilotJobOrder()!.PilotAgency(),
        persistedAssignments = lift(() => model.PilotJobOrder() && model.PilotJobOrder()!.Pilots() || []),
        // TH 02/09/2017 -- We have a Db unique index on the combo of PilotAssignment.PilotJobOrderId and PilotAssignment.AssignmentOrder.
        // Hence why it's safe to assume AssignmentOrder is unique here.
        persistedAssignmentByAssignmentOrder = uniqueIndex(persistedAssignments, pa => pa.AssignmentOrder()),
        pilots = lift(() => Array.from(data.Pilots.items()))
            .filter(p =>
                !!p.User() &&
                !!pilotAgency() &&
                p.User()!.Organization() === pilotAgency()!.Organization()
            )
            .map(p => PilotViewModel(p)),
        pilotsIdx = uniqueIndex(pilots, p => p.Model.Id()),
        pilotsBySection = nonuniqueIndex(pilots, p => SectionIndexKey(p.Model.SectionId())),
        pilotGroupingModel = (sectionId : () => number | null) => PilotGroupingViewModel(sectionId, pilotsBySection),
        pilotSections = lift(() => pilotAgency() && pilotAgency()!.Sections() || [])
            .orderBy(s => s.Order())
            .map(s => PilotSectionViewModel(s, pilotGroupingModel)),
        unassignedPilotSection = pilotGroupingModel(() => null),
        slotParams = SArray<AssignmentSlotParams>([]),
        // TH 02/08/2017 -- tempCollectionsDiry: Inspired by the tempcache trick from lib/DataCache.ts, but we need two extra ticks
        // to get the timing btwn the AssignmentSlotViewModel AutoSaver and the persistedAssignments.forEach call right.
        tempCollectionsDirtyTick1 = S.data<true>(true),
        tempCollectionsDirtyTick2 = S.data<true>(true),
        tempAddedPilotAssignments   = S.on(tempCollectionsDirtyTick2, () => new Set<PilotAssignment>()),
        tempDeletedPilotIds = S.on(tempCollectionsDirtyTick2, () => new Set<number>()),
        savingChanges = S.data(false),
        assignmentSlots = slotParams.map(params => AssignmentSlotViewModel(params, ajax, model, pilotsIdx, nextAvailableOrder, savingChanges, markAsAdded, markAsDeleted, removeSlot))
            .orderBy(slot => slot.Order()),
        assignedPilotsArray = assignmentSlots.filter(s => !!s.Pilot()).map(s => s.Pilot()!),
        assignedPilots = S(() => new Set(assignedPilotsArray())),
        assignablePilotIds = S(() => new Set(model.AssignablePilotIds()));

    // TH 02/08/2017 -- Sequence our ticks.
    // The goal is to get the new temp collections to get constructed AFTER we've checked
    // the old collections for added/deleted assignments.
    S(() => {
        tempCollectionsDirtyTick1();
        tempCollectionsDirtyTick2(true);
    });

    // TH 03/02/2017 -- We had to change the timing of this forEach call in order to use the
    // useCache option w/ our ajax calls. Hence why we now check a savingChanges flag.
    // See the putAssignment and deleteAssignment functions inside AssignmentSlotViewModel.
    persistedAssignments.forEach(assignment => {
        const doAddSlot = () => slotParams.push({
            assignment,
            proposedOrder: assignment.AssignmentOrder()
        });

        // TH 03/02/2017 -- Found a new PilotAssignment.
        // Are we currently saving?...
        if (savingChanges()) {
            // ... if so, need to subscribe to savingChanges...
            S(() => {
                const saving = S.sample(savingChanges);
                if (saving) {
                    // ... if still saving, call savingChanges to subscribe to future updates...
                    savingChanges();
                } else {
                    // ... once done saving, don't wanna subscribe to future updates.
                    // Sample the tempAddedPilotAssignments.
                    // If this assignment is in there, it means we just added this assignment
                    // from an existing slot. No need to create another slot.
                    // If it's not in the temp collection, then add a new slot.
                    S.sample(() => {
                        if (!tempAddedPilotAssignments().has(assignment)) doAddSlot();
                    });
                }
            });
        } else {
            // ... if we're not saving, then we can just add the new slot immediately.
            doAddSlot();
        }
    }, assignment => S.sample(() => {
        const doRemoveSlot = () => {
            const slot : AssignmentSlotViewModel | undefined = assignmentSlots().find(s => s.Model.PilotId() === assignment.PilotId());
            if (slot) removeSlot(slot.Params);
        };

        // TH 03/02/2017 -- Discovered that a PilotAssignment was deleted.
        // Are we currently saving?...
        if (savingChanges()) {
            // ... if so, need to subscribe to savingChanges...
            S(() => {
                const saving = S.sample(savingChanges);
                if (saving) {
                    // ... if still saving, call savingChanges to subscribe to future updates...
                    savingChanges();
                } else {
                    // ... once done saving, don't wanna subscribe to future updates.
                    // Sample tempDeletedPilotIds.
                    // If this assignment is in there, it means we just deleted this assignment
                    // from an existing slot. No need to delete the slot here.
                    // (If the user clicked to delete the whole slot, we'll handle removing the slot in the AssignmentSlotViewModel.Delete method.)
                    // If it's not in the temp collection, then remove the slot.
                    S.sample(() => {
                        if (!tempDeletedPilotIds().has(assignment.PilotId())) doRemoveSlot();
                    });
                }
            });
        } else {
            // ... if we're not saving, then we can just remove the slot immediately.
            doRemoveSlot();
        }
    }));

    return {
        Model: model,
        AssignmentSlots: assignmentSlots,
        PilotSections: pilotSections,
        UnassignedPilotSection: unassignedPilotSection,
        CanEditPilotAssignments: () => model.AssignablePilotIds().length > 0 && !!model.PilotJobOrder() && !model.PilotJobOrder()!.Cancellation(),
        AddAssignmentSlot,
        AnyAssignablePilots,
        PilotAssignableForSlot
    };

    function nextAvailableOrder(proposedOrder : number) {
        // TH 02/08/2017 -- Next available order # on or after the proposedOrder that doesn't
        // already have any assigned slots.
        // TH 02/09/2017 -- The intention here is to try do reasonable things if a user
        // creates some empty slots but then gets more persisted PilotAssignments from a poll request.
        // That assumes we're polling while the details popup is open (which is the current behavior)
        // and also that two users are editing the same PJO at once.
        // But concurrent edits open up lots of other issues, and in general we aren't doing much to
        // try to handle concurrent edits gracefully. So I'm not too concerned about getting this one perfect.
        while (persistedAssignmentByAssignmentOrder(proposedOrder)()) proposedOrder++;
        return proposedOrder;
    }

    function markAsAdded(assignment : PilotAssignment) {
        S.freeze(() => {
            tempAddedPilotAssignments().add(assignment);
            tempCollectionsDirtyTick1(true);
        });
    }

    function markAsDeleted(pilotId : number) {
        S.freeze(() => {
            tempDeletedPilotIds().add(pilotId);
            tempCollectionsDirtyTick1(true);
        });
    }

    function removeSlot(params : AssignmentSlotParams) {
        slotParams.remove(params);
    }

    function AddAssignmentSlot() {
        slotParams.push({
            assignment: null,
            proposedOrder: assignmentSlots().length ? Math.max(...assignmentSlots().map(s => s.Order())) + 1 : 0
        });
    }

    function AnyAssignablePilots(group : PilotGroupingViewModel, slot : AssignmentSlotViewModel) {
        return group.Pilots().some(p => PilotAssignableForSlot(p, slot));
    }

    function PilotAssignableForSlot(pilot : PilotViewModel, slot : AssignmentSlotViewModel) {
        return assignablePilotIds().has(pilot.Model.Id()) && !assignedPilots().has(pilot)
            || slot.Pilot() === pilot;
    }
}
export type PilotAssignments = ReturnType<typeof PilotAssignments>;

interface AssignmentSlotParams {
    assignment : PilotAssignment | null,
    proposedOrder : number
}

let AssignmentSlotReactId = 0;

// A slot that can hold 0 or 1 variable pilot assignments.
function AssignmentSlotViewModel(
    params : AssignmentSlotParams,
    ajax : IAjax,
    model : ServerPilotJobOrderDetails,
    pilotsIdx : UniqueIndex<PilotViewModel>,
    nextAvailableOrder : (proposedOrder : number) => number,
    savingChanges : DataSignal<boolean>,
    markAsAdded   : (assignment : PilotAssignment) => void,
    markAsDeleted : (pilotId : number) => void, 
    removeSlot : (params : AssignmentSlotParams) => void
) {
    const
        { assignment: initialAssignment, proposedOrder } = params,
        assignmentsUrlBase = () => model.PilotJobOrder() && model.PilotJobOrder()!.PilotsUrlBase() || '',
        assignmentUrl = (pilotId : number) => assignmentsUrlBase().replace('{pilotId}', '' + pilotId),
        persistedAssignment = S.value(initialAssignment),
        order = () => persistedAssignment() ? persistedAssignment()!.AssignmentOrder() : nextAvailableOrder(proposedOrder),
        effectiveAssignment = new PilotAssignment(initialAssignment ?
            S.sample(() => asOptions(initialAssignment)) :
            { PilotId: 0, PilotJobOrderId: 0 }
        ),
        // TH 02/08/2017 -- NOTE: We'll store the currently selected PilotId as draft.PilotId,
        // w/ 0 representing no pilot selected.
        { object: draft, methods: draftMethods } = DraftObject(effectiveAssignment, [
            'PilotId',
            'PilotConfirmed'
        ]);

    // TH 02/08/2017 -- Update effective assignment from persisted assignment.
    S(() => persistedAssignment() && PilotAssignment.update(effectiveAssignment, asOptions(persistedAssignment()!)));
    // TH 02/08/2017 -- Update effective order from order computation -- also flows through to draft for being persisted by the AutoSaver.
    S(() => effectiveAssignment.AssignmentOrder(order()));
    // TH 02/09/2017 -- Also had code that attempted to reset PilotConfirmed to false when PilotId changes.
    // But it led to complicated interactions w/ the AutoSaver, and I decided it wasn't worth pushing it at this time.
    // We also don't do that for any of the other Tentative/Confirmed toggles.

    AutoSaver(draftMethods.onWrite, async () => {
        const
            _persisted = persistedAssignment(),
            persistedId = _persisted && _persisted.PilotId(),
            currentDraft = asOptions(draft),
            selectedId = currentDraft.PilotId,
            draftCheckpoint = draftMethods.checkpoint();

        // TH 02/08/2017 -- firstReq and secondReq resolve w/ what the persistedAssignment
        // value should be after the request is finished.
        let firstReq = BBPromise.resolve(_persisted);
        if (persistedId && persistedId !== selectedId) {
            firstReq = deleteAssignment(persistedId).then(() => null);
        }

        let secondReq = firstReq;
        if (selectedId) {
            secondReq = firstReq.then(() => putAssignment(currentDraft));
        }

        return secondReq.then(assignment => S.freeze(() => {
            draftCheckpoint.revert();
            persistedAssignment(assignment);
        }));
    });

    return {
        Params: params,
        Model: draft,
        Pilot: pilotsIdx(draft.PilotId),
        Order: order,
        Delete,
        ReactKey: AssignmentSlotReactId++
    };

    function Delete() {
        if (savingChanges()) return;
        const persistedId = persistedAssignment() && persistedAssignment()!.PilotId();
        let req = BBPromise.resolve();
        if (persistedId) {
            req = deleteAssignment(persistedId);
        }
        // TH 02/08/2017 -- Remove the slot by removing its params object.
        req.then(() => removeSlot(params));
    }

    function putAssignment(assignment : PilotAssignmentOptions) {
        const client = ajax.Url<PilotAssignmentOptions, DataCacheUpdateEntities>(assignmentUrl(assignment.PilotId), {
                useCache: true
            });

        savingChanges(true);
        return client.Put(assignment).then(updates => S.freeze(() => {
            // TH 03/02/2017 -- Fish the new/updated PilotAssignment out of the DataCacheUpdate.
            // We expect exactly one PilotAssignment to be updated from this request.
            const serverAssignment = updates.PilotEntities() &&
                updates.PilotEntities()!.PilotAssignments()[0] || null;
            if (!serverAssignment) throw new Error('PutAssignmentSuccess -- Expected to find a new/update PilotAssignment');

            // TH 03/02/2017 -- UPDATE. We're now using the useCache option on our ajax client.
            // That's so we can pick up possible log events generated by this action.
            // As a result, this function now gets called AFTER the PCCache has been updated, after an async delay.
            // B/c of that, we had to change the timing of the persistedAssignments.forEach call above.
            //
            // TH 02/08/2017 -- markAsAdded to avoid adding a duplicate assignment slot.
            markAsAdded(serverAssignment);
            savingChanges(false);
            return serverAssignment;
        })).finally(() => savingChanges(false));
    }

    function deleteAssignment(pilotId : number) {
        const client = ajax.Url(assignmentUrl(pilotId), { useCache: true });

        savingChanges(true);
        return client.Delete().then(() => S.freeze(() => {
            // 03/02/2017 -- UPDATE. We're now using the useCache option on our ajax client.
            // That causes the same timing changes as for the putAssignment call. See above.
            //
            // TH 02/08/2017 -- markAsDeleted to avoid deleting this slot.
            // If this is a swap of pilots w/in a single slot, we definitely don't wanna delete the slot.
            // And if this is an actual deletion of the slot, we'll take care of removing the slot in the
            // Delete function above.
            markAsDeleted(pilotId);
            savingChanges(false);
        })).finally(() => savingChanges(false));
    }
}
export type AssignmentSlotViewModel = ReturnType<typeof AssignmentSlotViewModel>;

// TH 02/08/2017 -- Hmmm... A possible generic utility?
// Alternatively, do we maybe wanna bake functionality into auto-generated models for
// converting to a Data or Options type and/or adding an updateFromModel method?
function asOptions(assignment : PilotAssignment) {
    return JSON.parse(JSON.stringify(assignment)) as PilotAssignmentOptions;
}

// ViewModel properties that apply to a PilotSection entity or to the set of unassigned pilots
function PilotGroupingViewModel(
    sectionId : () => number | null,
    pilotsBySection : NonuniqueIndex<PilotViewModel>,
) {
    const indexKey = () => SectionIndexKey(sectionId());

    return {
        Pilots: lift(pilotsBySection(indexKey)).orderBy(p => p.Model.Order())
    };
}
export type PilotGroupingViewModel = ReturnType<typeof PilotGroupingViewModel>;

function SectionIndexKey(sectionId : number | null) {
    return sectionId === null ? NaN : sectionId;
}

// ViewModel for an actual PilotSection entity
function PilotSectionViewModel(
    pilotSection : PilotSection,
    pilotGroupingModel : (id : () => number | null) => PilotGroupingViewModel
) {
    const grouping = pilotGroupingModel(pilotSection.Id);

    return Object.assign(grouping, {
        Model: pilotSection
    });
}
export type PilotSectionViewModel = ReturnType<typeof PilotSectionViewModel>;

function PilotViewModel(
    pilot : Pilot
) {
    const
        userAbbrev = () => pilot.User()?.AbbreviatedName() ?? '',
        displayName = () => userAbbrev() + ' (' + pilot.EffectiveCode() + ')';

    return {
        Model: pilot,
        DisplayName: displayName
    };
}
export type PilotViewModel = ReturnType<typeof PilotViewModel>;


// WEBPACK FOOTER //
// ./apps/pilots/pilotjoborderdetails/pilotassignments.ts